import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormElements, NewDialog, NotifyIcon } from 'web-components';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Grid, InputAdornment } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { generateColor } from 'helpers/colorUtils';
import { useParams } from 'react-router-dom';
import { loadMachine } from 'redux/machines/actions';
import { getSelectedMachine } from 'redux/machines/selectors';
import { getData } from '../../../../../redux/rootSelectors';
import { loadMachinesSensorsTreeView } from '../../../../../redux/filters/actions';
import { getMachineSensorsTreeView } from '../../../../../redux/filters/selectors';

import { CustomTreeView } from '../../../../../components/CustomTreeView';
import { filterTreeBySelections, getDialogFilterOptions, keepSameArrayIfUnchanged } from '../dialogTreeFilters';

function AddSensorCard({ open, editingSensors, sensorsLength, handleCloseDialog, handleApply }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const treeView = useSelector(compose(getData, getMachineSensorsTreeView));
  const [selectedProductLines, setSelectedProductLines] = useState([]);
  const [selectedMachines, setSelectedMachines] = useState([]);
  const [selectedSensors, setSelectedSensors] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [clear, setClear] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const isApplyingRef = useRef(false);
  const didApplyInitialSelectionRef = useRef(false);

  const { id } = useParams();
  const { data: parentMachine } = useSelector(getSelectedMachine);
  const productionLines = treeView?.production_lines || [];
  const { productLineOptions, machineOptions, sensorOptions } = useMemo(
    () =>
      getDialogFilterOptions({
        productionLines,
        selectedProductLines,
        selectedMachines,
        t
      }),
    [productionLines, selectedProductLines, selectedMachines, t]
  );
  const filteredTreeData = useMemo(
    () =>
      filterTreeBySelections({
        productionLines,
        selectedProductLines,
        selectedMachines,
        selectedSensors
      }),
    [productionLines, selectedProductLines, selectedMachines, selectedSensors]
  );

  useEffect(() => {
    if (!parentMachine?.id) {
      dispatch(loadMachine(id));
    } else if (open && !didApplyInitialSelectionRef.current) {
      const preDefProdLineFilter = [{ title: parentMachine.production_line, value: parentMachine.production_line }];
      const preDefMachineFilter = [{ id: parentMachine.id, title: parentMachine.name, value: parentMachine.name }];
      didApplyInitialSelectionRef.current = true;
      setSelectedProductLines(preDefProdLineFilter);
      setSelectedMachines(preDefMachineFilter);
    }
  }, [parentMachine, dispatch, id, open]);

  useEffect(() => {
    if (!open) {
      didApplyInitialSelectionRef.current = false;
      setSelectedProductLines([]);
      setSelectedMachines([]);
      setSelectedSensors([]);
      setSelectedItems([]);
      setClear(false);
      return;
    }

    dispatch(
      loadMachinesSensorsTreeView({
        productionLines: [],
        sensors: [],
        machineIds: []
      })
    );
  }, [dispatch, open]);

  useEffect(() => {
    if (!open || !productionLines.length) {
      return;
    }

    setSelectedProductLines(prevValue =>
      keepSameArrayIfUnchanged(
        prevValue,
        prevValue.filter(item => productLineOptions.some(option => option.value === item.value)),
        item => item.value
      )
    );
    setSelectedMachines(prevValue =>
      keepSameArrayIfUnchanged(
        prevValue,
        prevValue.filter(item => machineOptions.some(option => option.id === item.id)),
        item => item.id
      )
    );
    setSelectedSensors(prevValue =>
      keepSameArrayIfUnchanged(
        prevValue,
        prevValue.filter(item => sensorOptions.some(option => option.type === item.type)),
        item => item.type
      )
    );
  }, [open, productionLines.length, productLineOptions, machineOptions, sensorOptions]);

  const handleProductLineChange = (_event, value) => {
    const previousValues = selectedProductLines
      .map(item => item.value)
      .sort()
      .join('|');
    const nextValues = value
      .map(item => item.value)
      .sort()
      .join('|');
    const didChangeToAnotherParent = value.length > 0 && previousValues !== nextValues;

    setSelectedProductLines(value);

    if (didChangeToAnotherParent) {
      setSelectedMachines([]);
      setSelectedSensors([]);
    }
  };

  const handleMachineChange = (_event, newValue) => {
    const previousValues = selectedMachines
      .map(item => item.id)
      .sort()
      .join('|');
    const nextValues = newValue
      .map(item => item.id)
      .sort()
      .join('|');
    const didChangeToAnotherParent = newValue.length > 0 && previousValues !== nextValues;

    setSelectedMachines(newValue);

    if (didChangeToAnotherParent) {
      setSelectedSensors([]);
    }
  };

  const handleSensorChange = (_event, newValue) => {
    setSelectedSensors(newValue);
  };

  const handleSelectionChange = items => {
    const flattenedChildren = items.flatMap((item, itemIndex) =>
      item.childrenNames.map((child, childIndex) => ({
        ...child,
        machineId: item.machineID,
        productLine: item.productLine,
        uniqueIndex: itemIndex * 100 + childIndex,
        machine: item.machine
      }))
    );

    const extractedData = flattenedChildren.map((child, idx) => ({
      temp_id: sensorsLength + idx + 1,
      type: child.type,
      name: child.name,
      production_line: child.productLine,
      machine_id: child.machineId,
      machine_name: child.machine,
      is_custom: child.is_custom,
      is_static: child.is_static,
      custom_icon_type: child.icon,
      line_color: generateColor(sensorsLength + idx + 1),
      line_style: 'LINE_STYLE_SOLID',
      line_thickness: 1,
      auto_scale: true,
      max_scale: null,
      min_scale: null
    }));

    setSelectedItems(extractedData);
  };

  const content = (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <FormElements.MultiSelect
            label={t('dialog.add_new_template.filter_by_product_line')}
            name="product_line"
            id="product_line"
            bgcolor="#EFF1F3"
            fullWidth
            value={selectedProductLines}
            setFieldValue={handleProductLineChange}
            options={productLineOptions}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" style={{ position: 'absolute', right: '12px', bottom: '22px' }}>
                  <NotifyIcon iconName="filterOutlined" fontSize="default" color="primary" />
                </InputAdornment>
              )
            }}
            data-testid="dialog-add-new-template-filter-product-line-field"
            clear={clear}
            setClear={setClear}
            sx={{
              mt: { md: 1 },
              mb: { md: 1 }
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormElements.MultiSelect
            label={t('dialog.add_new_template.filter_by_machine')}
            name="filter_machine"
            id="filter_machine"
            bgcolor="#EFF1F3"
            fullWidth
            multiple
            value={selectedMachines}
            setFieldValue={handleMachineChange}
            options={machineOptions}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" style={{ position: 'absolute', right: '12px', bottom: '22px' }}>
                  <NotifyIcon iconName="filterOutlined" fontSize="default" color="primary" />
                </InputAdornment>
              )
            }}
            data-testid="dialog-add-new-template-filter-product-line-field"
            clear={clear}
            setClear={setClear}
            sx={{
              mt: { md: 1 },
              mb: { md: 1 }
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormElements.MultiSelect
            label={t('dialog.add_new_template.filter_by_sensor')}
            name="filter_sensor"
            id="filter_sensor"
            bgcolor="#EFF1F3"
            fullWidth
            multiple
            value={selectedSensors}
            setFieldValue={handleSensorChange}
            options={sensorOptions}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" style={{ position: 'absolute', right: '12px', bottom: '22px' }}>
                  <NotifyIcon iconName="filterOutlined" fontSize="default" color="primary" />
                </InputAdornment>
              )
            }}
            data-testid="dialog-add-new-template-filter-product-line-field"
            clear={clear}
            setClear={setClear}
            sx={{
              mt: { md: 1 },
              mb: { md: 1 }
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <CustomTreeView
            rawData={filteredTreeData}
            editingSensors={editingSensors}
            selectedItems={selectedItems}
            onSelectionChange={handleSelectionChange}
            sensorsLength={sensorsLength}
          />
        </Grid>
      </Grid>
    </>
  );

  const handleClose = (...args) => {
    if (!isApplyingRef.current) {
      handleCloseDialog(...args);
    }
  };

  const handleOk = async () => {
    if (isApplyingRef.current) return;

    isApplyingRef.current = true;
    setIsApplying(true);

    try {
      await handleApply(selectedItems);
      handleCloseDialog();
    } finally {
      isApplyingRef.current = false;
      setIsApplying(false);
    }
  };

  return (
    <NewDialog
      open={open}
      handleClose={handleClose}
      handleCancel={handleClose}
      handleOk={handleOk}
      isSaving={isApplying}
      title={t('dialog.add_sensor.title')}
      subTitle={t('dialog.add_sensor.subtitle')}
      content={content}
      cancelCaption={t('default_actions.cancel')}
      okCaption={t('default_actions.apply')}
      actionBar={
        <>
          <Button
            data-testid="add-chart-template-sensors-cancel"
            sx={{ minHeight: 56, minWidth: 115 }}
            disabled={isApplying}
            onClick={handleClose}
            variant="text"
            color="error"
          >
            {t('default_actions.cancel')}
          </Button>
          <LoadingButton
            data-testid="add-chart-template-sensors-apply"
            sx={{ minHeight: 56, minWidth: 115 }}
            loading={isApplying}
            disabled={isApplying}
            onClick={handleOk}
            variant="contained"
          >
            {t('default_actions.apply')}
          </LoadingButton>
        </>
      }
      displayActionBar="flex"
      justifyContentActionBar="space-between"
      minWidth="60%"
      maxWidth="98%"
      maxHeight="98%"
    />
  );
}

AddSensorCard.propTypes = {
  open: PropTypes.bool.isRequired,
  editingSensors: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  sensorsLength: PropTypes.number.isRequired,
  handleCloseDialog: PropTypes.func.isRequired,
  handleApply: PropTypes.func.isRequired
};

export default AddSensorCard;
